import React, { useEffect, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import {
    Box, Button, Card, Checkbox, Chip, CircularProgress, Dialog, DialogContent,
    DialogTitle, FormControlLabel, IconButton, MenuItem, TextField, Typography, Paper,
} from '@material-ui/core'
import PrintIcon from '@material-ui/icons/Print'
import CloseIcon from '@material-ui/icons/Close'
import PictureAsPdfIcon from '@material-ui/icons/PictureAsPdf'
import Layout from '../components/layout/Layout'
import { useRecoilValue } from 'recoil'
import { userInfoState } from '../recoil/atoms'
import api from '../api/client'
import { toFarsiDigits, gregorianToJalali } from '../assets/utils'

const money = (v) => toFarsiDigits(Number(v || 0).toLocaleString('en-US'))

const Stat = ({ label, value, color, bg }) => (
    <Box style={{
        flex: '1 1 150px', background: bg || '#fff', border: '1px solid #e3e6f2',
        borderRadius: 14, padding: '12px 14px', boxShadow: '0 3px 12px rgba(26,35,126,.06)',
    }}>
        <Typography style={{ fontSize: 11.5, color: '#7b86a3' }}>{label}</Typography>
        <Typography style={{ fontSize: 17, fontWeight: 800, color: color || '#1a237e', direction: 'ltr', textAlign: 'right' }}>
            {value}
        </Typography>
    </Box>
)

const SORTS = [
    { value: 'date', label: 'تاریخ' },
    { value: 'amount', label: 'مبلغ پیش‌پرداخت' },
    { value: 'number', label: 'شمارهٔ حواله' },
]

const SCOPES_ALL = [
    { value: 'contract', label: 'همین قرارداد' },
    { value: 'contractor', label: 'همهٔ قراردادهای این پیمانکار' },
    { value: 'department', label: 'همهٔ قراردادهای این سازمان' },
]
// «همهٔ قراردادهای سازمان» فقط برای ادمین/سازمان/مدیر سازمان
const ORG_ROLES = ['Admin', 'Department', 'DepartmentManager']
const scopesFor = (role) => (ORG_ROLES.includes(role)
    ? SCOPES_ALL
    : SCOPES_ALL.filter((o) => o.value !== 'department'))

const PrepaymentReport = () => {
    const { id } = useParams()
    const [sp] = useSearchParams()
    // ctx=contractor ⇒ از «پروندهٔ یک پیمانکار» وارد شدهایم (پنل مدیر سازمان)
    const ctxContractor = sp.get('ctx') === 'contractor'
    const userInfo = useRecoilValue(userInfoState)
    const isContractor = (!!userInfo && userInfo.role === 'Contractor') || ctxContractor
    // در پنلِ پیمانکار، انتخابِ «دامنه» وجود ندارد (فقط همین قرارداد)
    const SCOPES = isContractor ? [] : scopesFor(userInfo && userInfo.role)
    const [data, setData] = useState(null)
    const [loading, setLoading] = useState(true)
    const [ptype, setPtype] = useState('')
    const [withDiff, setWithDiff] = useState(true)
    const [scope, setScope] = useState('contract')
    const [sort, setSort] = useState('date')
    const [dir, setDir] = useState('asc')
    const [from, setFrom] = useState('')
    const [to, setTo] = useState('')
    const [q, setQ] = useState('')
    const [detail, setDetail] = useState(null)

    const params = () => ({
        prepayment_type: ptype,
        with_diff: withDiff ? 1 : 0,
        scope,
        sort,
        dir,
        from,
        to,
        q,
        ctx: ctxContractor ? 'contractor' : '',
    })

    const load = async () => {
        setLoading(true)
        try {
            const r = await api.get(`contracts/${id}/prepayment-report/`, { params: params() })
            setData(r.data)
        } catch (e) {
            setData(null)
        } finally {
            setLoading(false)
        }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { load() }, [id, ptype, withDiff, scope, sort, dir, from, to, q])

    const rows = (data && data.rows) || []
    const s = (data && data.summary) || {}
    const byType = (data && data.summary_by_type) || []
    const types = (data && data.options && data.options.prepayment_types) || []
    const c = (data && data.contract) || {}
    const sc = (data && data.scope) || {}
    const multi = (sc.contracts || 1) > 1

    const download = async (kind) => {
        const p = new URLSearchParams()
        Object.entries(params()).forEach(([k, v]) => p.append(k, v))
        try {
            const r = await api.get(`contracts/${id}/prepayment-report/${kind}/`, {
                params: Object.fromEntries(p),
                responseType: 'blob',
            })
            const url = URL.createObjectURL(new Blob([r.data]))
            const a = document.createElement('a')
            a.href = url
            a.download = `prepayment_${(c.number || id).toString().replace(/\//g, '_')}.${kind === 'excel' ? 'xlsx' : 'pdf'}`
            document.body.appendChild(a)
            a.click()
            a.remove()
            URL.revokeObjectURL(url)
        } catch (e) {
            alert('دریافت فایل ناموفق بود. دوباره تلاش کنید.')
        }
    }

    const openPrint = () => {
        const p = new URLSearchParams()
        Object.entries(params()).forEach(([k, v]) => p.append(k, v))
        window.open(`/contracts/${id}/prepayment/print?${p.toString()}`, '_blank')
    }

    const heads = ['حواله']
    if (multi) heads.push('قرارداد')
    heads.push('تاریخ', 'سال مالی', 'نوع پیش‌پرداخت', 'پیش‌پرداخت')
    if (withDiff) heads.push('تفاوت نرخ روز')
    heads.push('تسویه', 'تسویهٔ سال‌های قبل')
    if (withDiff) heads.push('جمع پیش‌پرداخت')

    return (
        <Layout>
            <Box style={{ direction: 'rtl', padding: 8 }}>
                <Card elevation={0} style={{
                    borderRadius: 18, overflow: 'hidden', marginBottom: 16,
                    boxShadow: '0 8px 26px rgba(26,35,126,.10)',
                }}>
                    <Box style={{
                        background: 'linear-gradient(120deg,#1a237e,#3949ab 60%,#5c6bc0)',
                        color: '#fff', padding: '18px 22px',
                    }}>
                        <Typography style={{ fontWeight: 800, fontSize: 17 }}>
                            گزارش ریز حواله‌های پیش‌پرداخت
                        </Typography>
                        <Typography style={{ fontSize: 12, opacity: .9 }}>
                            قرارداد {toFarsiDigits(c.number || '')} — {c.contractor || ''}
                            {multi ? ` — ${toFarsiDigits(sc.contracts)} قرارداد در این گزارش` : ''}
                        </Typography>
                    </Box>

                    <Box display="flex" gap={1.5} flexWrap="wrap" alignItems="center" style={{ padding: 16 }}>
                        {!isContractor && (
                            <TextField
                                select size="small" variant="outlined" label="دامنهٔ گزارش"
                                value={scope} onChange={(e) => setScope(e.target.value)}
                                style={{ minWidth: 235 }}
                            >
                                {SCOPES.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                            </TextField>
                        )}
                        <TextField
                            select size="small" variant="outlined" label="نوع پیش‌پرداخت"
                            value={ptype} onChange={(e) => setPtype(e.target.value)}
                            style={{ minWidth: 200 }}
                        >
                            <MenuItem value="">همه</MenuItem>
                            {types.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                        </TextField>
                        <TextField
                            size="small" variant="outlined" label="از تاریخ"
                            placeholder="1405/01/01" value={from}
                            onChange={(e) => setFrom(e.target.value)}
                            style={{ width: 130 }} inputProps={{ dir: 'ltr' }}
                        />
                        <TextField
                            size="small" variant="outlined" label="تا تاریخ"
                            placeholder="1405/12/29" value={to}
                            onChange={(e) => setTo(e.target.value)}
                            style={{ width: 130 }} inputProps={{ dir: 'ltr' }}
                        />
                        <TextField
                            size="small" variant="outlined" label="جستجو (حواله/شرح/سند)"
                            value={q} onChange={(e) => setQ(e.target.value)}
                            style={{ minWidth: 200 }}
                        />
                        <TextField
                            select size="small" variant="outlined" label="مرتب‌سازی"
                            value={sort} onChange={(e) => setSort(e.target.value)}
                            style={{ minWidth: 165 }}
                        >
                            {SORTS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                        </TextField>
                        <TextField
                            select size="small" variant="outlined" label="جهت"
                            value={dir} onChange={(e) => setDir(e.target.value)}
                            style={{ minWidth: 110 }}
                        >
                            <MenuItem value="asc">صعودی</MenuItem>
                            <MenuItem value="desc">نزولی</MenuItem>
                        </TextField>
                        <FormControlLabel
                            control={<Checkbox checked={withDiff} onChange={(e) => setWithDiff(e.target.checked)} color="primary" />}
                            label={<span style={{ fontSize: 13, fontWeight: 700, color: '#1a237e' }}>تفاوت نرخ روز</span>}
                            style={{ background: '#f4f6ff', borderRadius: 10, padding: '2px 10px' }}
                        />
                        <Box style={{ flex: 1 }} />
                        <Button variant="contained" startIcon={<PrintIcon />} onClick={openPrint}
                            style={{ background: '#00695c', color: '#fff', fontWeight: 800, borderRadius: 10 }}>
                            گزارش چاپی
                        </Button>
                        <Button variant="contained" startIcon={<PictureAsPdfIcon />} onClick={() => download('pdf')}
                            style={{ background: '#b71c1c', color: '#fff', fontWeight: 800, borderRadius: 10 }}>
                            PDF
                        </Button>
                        <Button variant="contained" startIcon={<PrintIcon />} onClick={() => download('excel')}
                            style={{ background: '#1a237e', color: '#fff', fontWeight: 700, borderRadius: 10 }}>
                            خروجی اکسل
                        </Button>
                    </Box>
                </Card>

                <Box display="flex" gap={2} flexWrap="wrap" style={{ marginBottom: 16 }}>
                    <Stat label="تعداد حواله" value={toFarsiDigits(s.count || 0)} />
                    <Stat label="پیش‌پرداخت پرداختی" value={money(s.base)} />
                    {withDiff && <Stat label="تفاوت نرخ روز" value={money(s.diff)} color="#b45309" />}
                    {withDiff && <Stat label="جمع پیش‌پرداخت" value={money(s.amount)} color="#047857" bg="#f1fbf6" />}
                    <Stat label="تسویه" value={money(s.settled)} color="#c62828" />
                    <Stat label="مانده" value={money(s.pending)} color="#00695c" bg="#f1f8f8" />
                </Box>

                {byType.length > 0 && (
                    <Paper elevation={0} style={{ borderRadius: 16, marginBottom: 16, overflow: 'hidden', boxShadow: '0 4px 18px rgba(26,35,126,.06)' }}>
                        <Box style={{ padding: '10px 14px', background: '#eef1ff', fontWeight: 800, color: '#1a237e', fontSize: 13.5 }}>
                            جمع به تفکیک نوع پیش‌پرداخت
                        </Box>
                        <Box style={{ overflowX: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13.5 }}>
                                <thead>
                                    <tr style={{ background: '#f7f9ff', color: '#3949ab' }}>
                                        <th style={{ padding: '9px 8px' }}>نوع</th>
                                        <th style={{ padding: '9px 8px' }}>تعداد</th>
                                        <th style={{ padding: '9px 8px' }}>پیش‌پرداخت</th>
                                        {withDiff && <th style={{ padding: '9px 8px' }}>تفاوت نرخ روز</th>}
                                        <th style={{ padding: '9px 8px' }}>تسویه</th>
                                        {withDiff && <th style={{ padding: '9px 8px' }}>جمع</th>}
                                        <th style={{ padding: '9px 8px' }}>مانده</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {byType.map((b, i) => (
                                        <tr key={b.prepayment_type} style={{ background: i % 2 ? '#fafbff' : '#fff', textAlign: 'center' }}>
                                            <td style={{ padding: '8px', fontWeight: 700 }}>{b.prepayment_type}</td>
                                            <td style={{ padding: '8px' }}>{toFarsiDigits(b.count)}</td>
                                            <td style={{ padding: '8px' }}>{money(b.base)}</td>
                                            {withDiff && <td style={{ padding: '8px', color: '#b45309' }}>{money(b.diff)}</td>}
                                            <td style={{ padding: '8px', color: '#c62828' }}>{money(b.back + b.back_old)}</td>
                                            {withDiff && <td style={{ padding: '8px', color: '#047857', fontWeight: 800 }}>{money(b.amount)}</td>}
                                            <td style={{ padding: '8px', color: '#00695c', fontWeight: 800 }}>{money(b.pending)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </Box>
                    </Paper>
                )}

                <Paper elevation={0} style={{ borderRadius: 16, overflow: 'hidden', boxShadow: '0 4px 18px rgba(26,35,126,.06)' }}>
                    {loading ? (
                        <Box p={4} textAlign="center"><CircularProgress size={28} /></Box>
                    ) : rows.length === 0 ? (
                        <Box p={4} textAlign="center">
                            <Typography style={{ color: '#7b86a3' }}>برای این فیلترها داده‌ای یافت نشد.</Typography>
                        </Box>
                    ) : (
                        <Box style={{ overflowX: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 15 }}>
                                <thead>
                                    <tr style={{ background: '#1a237e', color: '#fff' }}>
                                        {heads.map((h) => (
                                            <th key={h} style={{ padding: '13px 10px', fontWeight: 800, fontSize: 14 }}>{h}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.map((r, i) => (
                                        <tr key={r.id} onClick={() => setDetail(r)}
                                            title="جزئیات حواله"
                                            style={{ background: i % 2 ? '#fafbff' : '#fff', cursor: 'pointer' }}>
                                            <td style={{ padding: '11px 10px', textAlign: 'center', color: '#1a237e', fontWeight: 700 }}>
                                                {toFarsiDigits(r.number)}
                                            </td>
                                            {multi && <td style={{ padding: '11px 10px', textAlign: 'center' }}>{toFarsiDigits(r.contract_number)}</td>}
                                            <td style={{ padding: '11px 10px', textAlign: 'center' }}>{gregorianToJalali(r.date)}</td>
                                            <td style={{ padding: '11px 10px', textAlign: 'center' }}>{toFarsiDigits(r.fiscal_year || '')}</td>
                                            <td style={{ padding: '11px 10px', textAlign: 'center' }}>
                                                <Chip size="small" label={r.prepayment_type || '—'}
                                                    style={{ background: '#e8eafd', color: '#1a237e', fontWeight: 700, fontSize: 13 }} />
                                            </td>
                                            <td style={{ padding: '11px 10px', textAlign: 'center' }}>{money(r.base)}</td>
                                            {withDiff && <td style={{ padding: '11px 10px', textAlign: 'center', color: '#b45309' }}>{money(r.diff)}</td>}
                                            <td style={{ padding: '11px 10px', textAlign: 'center', color: '#c62828' }}>{money(r.back)}</td>
                                            <td style={{ padding: '11px 10px', textAlign: 'center', color: '#ad1457' }}>{money(r.back_old)}</td>
                                            {withDiff && (
                                                <td style={{ padding: '11px 10px', textAlign: 'center', fontWeight: 800, color: '#047857' }}>{money(r.amount)}</td>
                                            )}
                                        </tr>
                                    ))}
                                    <tr style={{ background: '#fff3e0', fontWeight: 800 }}>
                                        <td colSpan={multi ? 5 : 4} style={{ padding: '13px 10px', textAlign: 'center', fontSize: 14.5 }}>جمع کل</td>
                                        <td style={{ padding: '13px 10px', textAlign: 'center', fontSize: 14.5 }}>{money(s.base)}</td>
                                        {withDiff && <td style={{ padding: '13px 10px', textAlign: 'center', color: '#b45309', fontSize: 14.5 }}>{money(s.diff)}</td>}
                                        <td style={{ padding: '13px 10px', textAlign: 'center', color: '#c62828', fontSize: 14.5 }}>{money(s.back)}</td>
                                        <td style={{ padding: '13px 10px', textAlign: 'center', color: '#ad1457', fontSize: 14.5 }}>{money(s.back_old)}</td>
                                        {withDiff && <td style={{ padding: '13px 10px', textAlign: 'center', color: '#047857', fontSize: 14.5 }}>{money(s.amount)}</td>}
                                    </tr>
                                    <tr style={{ background: '#e8f5e9', fontWeight: 800 }}>
                                        <td colSpan={heads.length - 1} style={{ padding: '13px 10px', textAlign: 'center', fontSize: 14.5 }}>
                                            مانده (جمع پیش‌پرداخت − تسویه)
                                        </td>
                                        <td style={{ padding: '13px 10px', textAlign: 'center', color: '#00695c', fontSize: 14.5 }}>{money(s.pending)}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </Box>
                    )}
                </Paper>
            </Box>

            <Dialog open={!!detail} onClose={() => setDetail(null)} maxWidth="sm" fullWidth>
                <DialogTitle>
                    <Box display="flex" alignItems="center">
                        <Box flex={1} style={{ fontWeight: 800 }}>
                            حوالهٔ {detail ? toFarsiDigits(detail.number) : ''}
                        </Box>
                        <IconButton size="small" onClick={() => setDetail(null)}><CloseIcon /></IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent dividers style={{ direction: 'rtl' }}>
                    {detail && (
                        <Box style={{ fontSize: 14, lineHeight: 2.4 }}>
                            <div>قرارداد: <b>{toFarsiDigits(detail.contract_number)}</b></div>
                            <div>تاریخ: <b>{gregorianToJalali(detail.date)}</b> — سال مالی: {toFarsiDigits(detail.fiscal_year || '—')}</div>
                            <div>نوع پیش‌پرداخت: <b>{detail.prepayment_type || '—'}</b></div>
                            <div>پیش‌پرداخت: <b>{money(detail.base)}</b> ریال</div>
                            {withDiff && <div>تفاوت نرخ روز: <b>{money(detail.diff)}</b> ریال</div>}
                            <div>تسویه: <b>{money(detail.back)}</b> ریال — سال‌های قبل: <b>{money(detail.back_old)}</b> ریال</div>
                            <div>جمع پیش‌پرداخت: <b>{money(detail.amount)}</b> ریال</div>
                            <div>مانده: <b style={{ color: '#00695c' }}>{money(detail.pending)}</b> ریال</div>
                            <div>شمارهٔ سند: {toFarsiDigits(detail.document_number || '—')}</div>
                            <div>شرح: {detail.remark || '—'}</div>
                        </Box>
                    )}
                </DialogContent>
            </Dialog>
        </Layout>
    )
}

export default PrepaymentReport
